import {
  getActiveFilters,
  getFilterablePriceValue,
  getGroupedFilterableValues,
  groupFiltersByKey,
  serializeFilters,
  transformMinAndMaxPriceToFilter,
  transformStateToFilters,
  transformToWhereCondition,
  type CentAmount,
  type Filter,
  type FilterParams,
  type TransformedFilter,
} from '@scayle/storefront-nuxt'
import type { FiltersEndpointResponseData } from '@scayle/storefront-api'
import { omit, debounce } from 'radash'

export type FilterState = ObjectWith<{
  prices: [number, number]
}>

export default (
  filters: FiltersEndpointResponseData,
  refreshProductCount: ({ where }: FilterParams) => Promise<void>,
) => {
  const i18n = useI18n()
  const currentShop = useCurrentShop()
  const { toggle } = useSlideIn('FilterSlideIn')
  const { activeFilters } = useQueryFilterState()
  const router = useRouter()
  const route = useRoute()

  const { removeFaceWidth } = useFaceWidth()

  const scope = effectScope()
  tryOnScopeDispose(() => scope.stop())

  const setVtoFilters = (filters: any[]) => {
    const onlyTrue = filters.find((i) => i.displayName.toLowerCase() === 'true')
    if (onlyTrue) {
      onlyTrue.displayName = i18n.t('filter.available')
      return [onlyTrue]
    }
    return filters
  }

  const removeFiltersWithEmptyValues = (filters: Record<string, any[]>) => {
    return (
      Object.keys(filters)?.reduce<Record<string, any[]>>((acc, key) => {
        if (filters[key].length < 1) {
          return acc
        }

        return Object.assign(acc, { [key]: filters[key] })
      }, {}) ?? {}
    )
  }

  const supportedFilters = computed(() => [
    'brand',
    'prices',
    ...filters
      .map((f) => f.slug)
      .filter(
        (f) =>
          !['brand', 'prices', 'sale', 'max_savings_percentage'].includes(f),
      ),
  ])
  const availableFilterValues = computed(() => {
    const objv = getGroupedFilterableValues(supportedFilters.value, filters)

    if (supportedFilters.value.includes('virtualTryOnReady')) {
      objv.virtualTryOnReady = setVtoFilters(objv.virtualTryOnReady)
    }
    return removeFiltersWithEmptyValues(objv)
  })

  /**
   * Remove mapped filter values, add ids prop with all included ids and rewrite displayName if needed
   * (only if we have an option which should be removed and the filter where it should be mapped on is not available)
   * @param filterSlug filter slug
   * @param values available filter values
   */
  const removeMappedFilterValues = (filterSlug: string, values: any[]) => {
    const hasKey = (key: string, arr: any[] = []) =>
      arr.some((value) => value.displayName === key)

    if (Object.keys(filterMap).includes(filterSlug)) {
      return (
        values?.reduce((res, data) => {
          const [key] =
            Object.entries(filterMap[filterSlug]).find(([, group]) =>
              group.includes(data.displayName),
            ) || []

          if (!key) {
            return [...res, { ...data, ids: [data.value] }]
          }

          if (!hasKey(key, res) && !hasKey(key, values)) {
            return [...res, { ...data, displayName: key, ids: [data.value] }]
          }

          const index = res.findIndex((value: any) => value.displayName === key)
          if (index !== -1) {
            return [
              ...res.slice(0, index),
              { ...res[index], ids: [...(res[index].ids || []), data.value] },
              ...res.slice(index + 1),
            ]
          }

          return res
        }, []) ?? []
      )
    } else {
      return values
    }
  }

  const getFilterIconPath = (slug: string): string => {
    const mapping: Record<string, string> = {
      shape: 'FrameShape',
      faceShape: 'FaceShape',
      rimType: 'RimType',
      lensBaseColor: 'Color',
      searchColorEcom: 'Color',
      baseColorLenses: 'Color',
      virtualTryOnReady: 'Vto',
      headWidth: 'HeadWidth',
    }
    if (mapping[slug]) {
      return 'Icon' + mapping[slug]
    }
    return ''
  }

  // combines filter attributes, values and applied order from "supportedFilters"
  const availableFilters = computed(() => {
    const processedFilters =
      filters
        ?.reduce<any[]>((acc, fil) => {
          if (fil.slug && availableFilterValues.value[fil.slug]) {
            const iconPath = getFilterIconPath(fil.slug)
            return [
              ...acc,
              {
                ...fil,
                values: removeMappedFilterValues(
                  fil.slug,
                  availableFilterValues.value[fil.slug],
                ).map((filterItem: any) => {
                  /**
                   * getGroupedFilterableValues from SFC strips the "value" attribute
                   * from the filterable values which we can use to map the filter values
                   * to correct icons. We need to add it back here.
                   */
                  const filterValues: any[] =
                    filters.find((vals) => vals.slug === fil.slug)?.values ?? []
                  const wholeFilterItem = filterValues.find(
                    (item) => item.name === filterItem.displayName,
                  )

                  const handle = wholeFilterItem
                    ? // different formatting of this attribute in different envs, this accounts for that
                      replaceUmlaute(wholeFilterItem.value).split('_')[0]
                    : // virtualTryOn special case
                      replaceUmlaute(filterItem.displayName).toLowerCase()

                  return {
                    ...filterItem,
                    handle,
                    iconName:
                      iconPath +
                      handle.charAt(0).toUpperCase() +
                      handle.slice(1),
                  }
                }),
                order: supportedFilters.value.indexOf(fil.slug),
                iconPath,
              },
            ]
          }

          return acc
        }, [])
        ?.sort((a, b) => a.order - b.order) ?? []

    // sort numberOfLenses filter values
    processedFilters.forEach((filter) => {
      if (filter.slug === 'numberOfLenses') {
        filter.values.sort(
          (a: any, b: any) =>
            parseInt(a.displayName, 10) - parseInt(b.displayName, 10),
        )
      }
    })

    return processedFilters
  })

  const minPrice = computed(() => {
    if (!availableFilterValues?.value?.prices) {
      return 0
    }

    return getFilterablePriceValue(availableFilterValues.value, 'min')
  })

  const maxPrice = computed(() => {
    if (!availableFilterValues?.value?.prices) {
      return 1000000
    }
    return getFilterablePriceValue(availableFilterValues.value, 'max')
  })

  const getInitialState = (): FilterState => ({
    ...Object.fromEntries(filters.map((filter) => [filter.slug, []])),
    prices: [minPrice.value, maxPrice.value],
  })

  const initialState = useState<FilterState>('filters', () => getInitialState())

  // user's selected conditions
  const state = useState<FilterState>(`state-filters`, () => ({
    ...initialState.value,
  }))

  const glassesWidthValues = computed<any[] | undefined>(() => {
    if (Array.isArray(availableFilterValues.value.glassesWidth)) {
      return availableFilterValues.value.glassesWidth.sort(
        (a: any, b: any) =>
          parseInt(a.displayName, 10) - parseInt(b.displayName, 10),
      )
    }
  })

  const minMaxGlassesWidth = computed(() =>
    glassesWidthValues.value
      ? [
          parseInt(glassesWidthValues.value.at(0).displayName, 10),
          parseInt(glassesWidthValues.value.at(-1).displayName, 10),
        ]
      : [0, 0],
  )

  const glassesWidthState = ref<[number, number]>([
    minMaxGlassesWidth.value[0],
    minMaxGlassesWidth.value[1],
  ])

  const setInitialGlassesWidthSliderState = () => {
    if (
      Array.isArray(state.value.glassesWidth) &&
      state.value.glassesWidth.length
    ) {
      const sorted: any[] = state.value.glassesWidth.sort(
        (a: any, b: any) =>
          parseInt(a.displayName, 10) - parseInt(b.displayName, 10),
      )
      const min = parseInt(sorted.at(0).displayName, 10)
      const max = parseInt(sorted.at(-1).displayName, 10)

      glassesWidthState.value = [min, max]
    }
  }

  scope.run(() => {
    if (import.meta.server) {
      return
    }
    watch(glassesWidthState, () => {
      const [minVal, maxVal] = glassesWidthState.value
      if (glassesWidthValues?.value && minVal && maxVal) {
        if (
          minVal === minMaxGlassesWidth.value[0] &&
          maxVal === minMaxGlassesWidth.value[1]
        ) {
          state.value.glassesWidth = []
        } else {
          const valuesInRange = glassesWidthValues.value.filter(
            (f) =>
              parseInt(f.displayName, 10) >= minVal &&
              parseInt(f.displayName, 10) <= maxVal,
          )
          state.value.glassesWidth = valuesInRange
        }
      }
    })
  })

  const isSale = computed(() => {
    if (Array.isArray(state.value.sale)) {
      return state.value.sale.length > 0
    }

    return !!state.value.sale.length
  })

  const getFilterValue = () => {
    const transformedFilters: Record<string, number[]> =
      transformStateToFilters(
        removeEmpty(omit(state.value, ['prices', 'sale'])),
      )
    const transformedFiltersWithMappedValues: Record<string, number[]> = {}
    Object.keys(transformedFilters).forEach((filterSlug) => {
      const value = handleMappedFilterValues(
        filterSlug,
        transformedFilters[filterSlug],
      )
      if (!Array.isArray(value) || value.length > 0) {
        transformedFiltersWithMappedValues[filterSlug] = value
      }
    })

    return {
      ...transformedFiltersWithMappedValues,
      ...(priceRangeChanged.value &&
        transformMinAndMaxPriceToFilter(
          state.value.prices as unknown as (CentAmount | undefined)[],
        )),

      ...(isSale.value && { sale: true }),
    }
  }

  const onFilterStateChanged = async () => {
    const value = getFilterValue()
    await refreshProductCount({
      where: transformToWhereCondition(value),
    })
  }

  const debouncedEmitFilterStateChanged = debounce(
    { delay: 200 },
    onFilterStateChanged,
  )

  scope.run(() => {
    if (import.meta.server) {
      return
    }

    watch(
      state,
      () => {
        debouncedEmitFilterStateChanged()
      },
      { deep: true },
    )
  })

  const setActiveFiltersInState = (filters: TransformedFilter[]) => {
    const groupedActiveFilters = groupFiltersByKey(filters)

    groupedActiveFilters.forEach(([key, value]: [keyof FilterState, any]) => {
      state.value[key] =
        typeof value[0].value === 'boolean' ? value[0].value === true : value
    })
  }

  const setActivePriceRangeInState = (min: CentAmount, max: CentAmount) => {
    if (min) {
      state.value.prices[0] = min
    }

    if (max) {
      state.value.prices[1] = max
    }
  }

  const setStateFromUrlParams = () => {
    const availableValues = Object.fromEntries(
      availableFilters.value.map((filter) => [filter.slug, filter.values]),
    )

    setActiveFiltersInState(
      getActiveFilters(availableValues, activeFilters.value),
    )

    setActivePriceRangeInState(
      activeFilters.value.minPrice as CentAmount,
      activeFilters.value.maxPrice as CentAmount,
    )

    setInitialGlassesWidthSliderState()
  }

  const setFaceWidthFilter = (width: number) => {
    const [minWidth, maxWidth] = minMaxGlassesWidth.value

    // Ensure faceWidth is within the valid range
    const clampedWidth = Math.max(minWidth, Math.min(width, maxWidth))

    glassesWidthState.value = [clampedWidth, maxWidth]

    const value = glassesWidthValues.value?.filter(
      (item) => parseInt(item.displayName, 10) >= clampedWidth,
    )

    if (value) {
      state.value.glassesWidth = value
    }
  }

  const priceRangeChanged = computed(
    () => !isEqual(initialState.value.prices, state.value.prices),
  )

  const handleMappedFilterValues = (
    filterSlug: string,
    checkedFilterValueIds: number[],
  ) => {
    if (Object.keys(filterMap).includes(filterSlug)) {
      const currentFilter = availableFilters.value.find(
        (filter) => filter.slug === filterSlug,
      )

      if (currentFilter) {
        const currentFilterVals = [...(currentFilter.values as [])]

        return checkedFilterValueIds
          .flatMap((id) => {
            const group: any = currentFilterVals.find((filterVal: any) =>
              filterVal?.ids?.includes(id),
            )
            if (group) {
              return group?.ids ?? []
            }

            return [id]
          })
          .filter(Number)
      }
    }

    return checkedFilterValueIds
  }

  const _applyFilters = async (filter?: Filter, scrollToTop = true) => {
    const newQuery = {
      sort: route.query.sort,
      term: route.query.term,

      ...(filter ? serializeFilters(filter) : {}),
    }

    if (isEqual(route.query, newQuery)) {
      return
    }

    await router.push({
      path: route.path,
      query: { ...newQuery },
    })

    if (scrollToTop) {
      window.scroll({
        behavior: 'smooth',
        top: 0,
      })
    }
  }

  const applyFilters = () => {
    const value = getFilterValue()
    _applyFilters(value)
    toggle()
  }

  const applyFiltersWithoutToggle = () => {
    const value = getFilterValue()
    _applyFilters(value)
  }

  function resetFilterStatePrice() {
    state.value.prices = [minPrice.value, maxPrice.value]
  }

  const resetFilter = (key: keyof FilterState) => {
    state.value[key] = [...initialState.value[key]]
    if (key === 'prices') {
      resetFilterStatePrice()
    }
    if (key === 'glassesWidth') {
      glassesWidthState.value = [
        minMaxGlassesWidth.value[0],
        minMaxGlassesWidth.value[1],
      ]
      removeFaceWidth()
    }
  }

  const resetFilters = () => {
    state.value = deepClone(initialState.value)
    // Reset glassesWidth slider to initial values
    glassesWidthState.value = [
      minMaxGlassesWidth.value[0],
      minMaxGlassesWidth.value[1],
    ]
    removeFaceWidth()
  }

  const toCurrencyWithoutFractionDigits = (
    value: CentAmount | number,
  ): string => {
    return toLocaleCurrency(value as CentAmount, currentShop.value, {
      maximumFractionDigits: 0,
    })
  }

  const replaceUmlaute = (str: string) => {
    // replace them just with the vowel, because this is how the files are named like
    if (!str) {
      return ''
    }
    return str
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/Ä/g, 'A')
      .replace(/Ö/g, 'O')
      .replace(/Ü/g, 'U')
      .replace(/ß/g, 'ss')
  }

  return {
    resetFilter,
    resetFilters,
    state,
    minPrice,
    maxPrice,
    activeFilters,
    availableFilters,
    glassesWidthState,
    glassesWidthValues,
    setStateFromUrlParams,
    setFaceWidthFilter,
    minMaxGlassesWidth,
    toCurrencyWithoutFractionDigits,
    applyFilters,
    applyFiltersWithoutToggle,
  }
}
